import { supabase } from '@/integrations/supabase/client';

export interface DataMigration {
  id: string;
  version: string;
  description: string;
  execute: (userId: string) => Promise<void>;
  feature?: {
    name: string;
    description: string;
    icon?: string;
  };
}

/**
 * Registro de todas as migrações de dados disponíveis
 * IMPORTANTE: Adicione novas migrações ao final da lista para manter ordem de execução
 */
export const DATA_MIGRATIONS: DataMigration[] = [
  {
    id: 'add_pillars_array_to_habits',
    version: '1.0.0',
    description: 'Adiciona campo pillars[] a hábitos que só têm pilar único',
    execute: async (userId: string) => {
      // Buscar hábitos sem pillars preenchido
      const { data: habits, error } = await supabase
        .from('user_habits')
        .select('id, pilar, pillars')
        .eq('user_id', userId);
      
      if (error) throw error;
      
      // Atualizar cada hábito que precise
      for (const habit of habits || []) {
        if (!habit.pillars || habit.pillars.length === 0) {
          await supabase
            .from('user_habits')
            .update({ pillars: [habit.pilar] })
            .eq('id', habit.id);
        }
      }
    }
  },
  {
    id: 'add_pillars_array_to_goals',
    version: '1.0.0',
    description: 'Adiciona campo pillars[] a metas que só têm pilar único',
    execute: async (userId: string) => {
      const { data: goals, error } = await supabase
        .from('user_goals')
        .select('id, pilar, pillars')
        .eq('user_id', userId);
      
      if (error) throw error;
      
      for (const goal of goals || []) {
        if (!goal.pillars || goal.pillars.length === 0) {
          await supabase
            .from('user_goals')
            .update({ pillars: [goal.pilar] })
            .eq('id', goal.id);
        }
      }
    }
  },
  {
    id: 'normalize_habit_frequency',
    version: '1.0.0',
    description: 'Normaliza valores de frequency (diaria->daily, semanal->weekly)',
    execute: async (userId: string) => {
      const { data: habits, error } = await supabase
        .from('user_habits')
        .select('id, frequency')
        .eq('user_id', userId)
        .in('frequency', ['diaria', 'semanal', 'mensal']);
      
      if (error) throw error;
      
      const frequencyMap: Record<string, string> = {
        'diaria': 'daily',
        'semanal': 'weekly',
        'mensal': 'monthly'
      };
      
      for (const habit of habits || []) {
        const newFrequency = frequencyMap[habit.frequency];
        if (newFrequency) {
          await supabase
            .from('user_habits')
            .update({ frequency: newFrequency })
            .eq('id', habit.id);
        }
      }
    }
  },
  {
    id: 'ensure_completed_dates_array',
    version: '1.0.0',
    description: 'Garante que challenges tenham completed_dates como array',
    execute: async (userId: string) => {
      const { data: challenges, error } = await supabase
        .from('user_challenges')
        .select('id, completed_dates')
        .eq('user_id', userId)
        .is('completed_dates', null);
      
      if (error) throw error;
      
      for (const challenge of challenges || []) {
        await supabase
          .from('user_challenges')
          .update({ completed_dates: [] })
          .eq('id', challenge.id);
      }
    }
  },
  {
    id: 'init_offline_cache_v1',
    version: '1.0.0',
    description: 'Inicializa sistema de cache offline (IndexedDB)',
    execute: async (userId: string) => {
      // Esta migração apenas marca que o usuário foi migrado para o novo sistema
      // O cache será populado automaticamente no próximo carregamento
      console.log('✅ Sistema de cache offline inicializado para usuário:', userId);
    },
    feature: {
      name: '📱 Modo Offline',
      description: 'Agora você pode usar o app mesmo sem internet! Seus dados são salvos localmente e sincronizados quando você reconectar.',
      icon: '📱'
    }
  }
];

/**
 * Verifica quais migrações ainda não foram executadas para um usuário
 */
export async function getPendingMigrations(userId: string): Promise<DataMigration[]> {
  const { data: executedMigrations, error } = await supabase
    .from('data_migrations')
    .select('migration_id')
    .eq('user_id', userId)
    .eq('success', true);
  
  if (error) {
    console.error('Erro ao buscar migrações executadas:', error);
    return DATA_MIGRATIONS; // Em caso de erro, executar todas
  }
  
  const executedIds = new Set(executedMigrations?.map(m => m.migration_id) || []);
  return DATA_MIGRATIONS.filter(migration => !executedIds.has(migration.id));
}

/**
 * Executa uma migração de dados para um usuário
 */
export async function executeMigration(
  userId: string,
  migration: DataMigration
): Promise<{ success: boolean; error?: string }> {
  const startTime = performance.now();
  
  try {
    console.log(`🔄 Executando migração: ${migration.id} (v${migration.version})`);
    console.log(`   Descrição: ${migration.description}`);
    
    await migration.execute(userId);
    
    const executionTime = Math.round(performance.now() - startTime);
    
    // Registrar sucesso
    await supabase.from('data_migrations').insert({
      user_id: userId,
      migration_id: migration.id,
      migration_version: migration.version,
      success: true,
      execution_time_ms: executionTime,
      metadata: { description: migration.description }
    });
    
    console.log(`✅ Migração ${migration.id} concluída em ${executionTime}ms`);
    return { success: true };
    
  } catch (error) {
    const executionTime = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    console.error(`❌ Erro na migração ${migration.id}:`, errorMessage);
    
    // Registrar falha
    await supabase.from('data_migrations').insert({
      user_id: userId,
      migration_id: migration.id,
      migration_version: migration.version,
      success: false,
      error_message: errorMessage,
      execution_time_ms: executionTime,
      metadata: { description: migration.description }
    });
    
    return { success: false, error: errorMessage };
  }
}

/**
 * Executa todas as migrações pendentes para um usuário
 */
export async function runPendingMigrations(userId: string): Promise<{
  total: number;
  successful: number;
  failed: number;
  errors: string[];
}> {
  console.log('🚀 Verificando migrações de dados pendentes...');
  
  const pendingMigrations = await getPendingMigrations(userId);
  
  if (pendingMigrations.length === 0) {
    console.log('✅ Nenhuma migração pendente');
    return { total: 0, successful: 0, failed: 0, errors: [] };
  }
  
  console.log(`📋 ${pendingMigrations.length} migração(ões) pendente(s)`);
  
  const results = {
    total: pendingMigrations.length,
    successful: 0,
    failed: 0,
    errors: [] as string[]
  };
  
  for (const migration of pendingMigrations) {
    const result = await executeMigration(userId, migration);
    
    if (result.success) {
      results.successful++;
    } else {
      results.failed++;
      if (result.error) {
        results.errors.push(`${migration.id}: ${result.error}`);
      }
    }
  }
  
  console.log(`📊 Resultado das migrações:`, {
    total: results.total,
    sucesso: results.successful,
    falhas: results.failed
  });
  
  return results;
}

/**
 * Obtém histórico de migrações executadas para um usuário
 */
export async function getMigrationHistory(userId: string) {
  const { data, error } = await supabase
    .from('data_migrations')
    .select('*')
    .eq('user_id', userId)
    .order('executed_at', { ascending: false });
  
  if (error) {
    console.error('Erro ao buscar histórico de migrações:', error);
    return [];
  }
  
  return data || [];
}

/**
 * Força re-execução de uma migração específica (útil para debugging)
 */
export async function rerunMigration(userId: string, migrationId: string): Promise<void> {
  // Remover registro de execução anterior
  await supabase
    .from('data_migrations')
    .delete()
    .eq('user_id', userId)
    .eq('migration_id', migrationId);
  
  // Buscar migração
  const migration = DATA_MIGRATIONS.find(m => m.id === migrationId);
  if (!migration) {
    throw new Error(`Migração ${migrationId} não encontrada`);
  }
  
  // Executar novamente
  await executeMigration(userId, migration);
}
